/**
 * What happens to a batch of uploaded case documents: every one of them is extracted when the
 * caller asks — no upload, an audio file included, is processed by the server on its own
 * (ADR-0076) — and that one run is followed to its end. Pure, with the requests handed in.
 */
import type { CaseDocumentUploadResponse } from '@/services/api/cases'

/** What of an upload's answer the extraction needs. */
export type UploadedDocument = Pick<CaseDocumentUploadResponse, 'document_id'>

export interface UploadProcessingRequests {
  /** `POST /extract` for these documents; answers the run it started. */
  extract: (documentIds: string[]) => Promise<{ extraction_run_id: string }>
  /**
   * Settles once every one of the runs has ended; rejects when one failed — after all of them
   * ended, so the caller re-reads a settled case.
   */
  waitForRuns: (runIds: string[]) => Promise<void>
}

/**
 * Has the batch extracted and waits for it. Nothing to do for an empty batch. Rejects when the
 * extraction cannot be started or the wait for the run fails.
 */
export async function processUploads(
  uploads: readonly UploadedDocument[],
  requests: UploadProcessingRequests
): Promise<void> {
  const documentIds = uploads.map(upload => upload.document_id)
  if (documentIds.length === 0) return
  const { extraction_run_id } = await requests.extract(documentIds)
  await requests.waitForRuns([extraction_run_id])
}
